<script setup lang="ts">
import { computed, useTemplateRef, type TextareaHTMLAttributes } from 'vue';
import { type BaseTextInputProps } from './internal';
import NxInputWrapper from './NxInputWrapper.vue';

type TextareaProps = BaseTextInputProps & {
  /**
   * The value is not valid (a validation finding): the field takes the
   * kit's error border and the textarea `aria-invalid="true"`. Not a save
   * status — that is NxLoadingWrapper's. Defaults to `false`.
   */
  invalid?: boolean;
  /**
   * The browser's spell checker (red underlines). Off by default, which suits
   * codes and technical text; switch it on for prose people type — chat
   * messages, notes, descriptions. Defaults to `false`.
   */
  spellcheck?: boolean;
  /**
   * Whether the user may drag the field taller or wider. Defaults to
   * `true`; `false` keeps the height the field was given (`rows`).
   */
  resizable?: boolean;
  /** Fill a bounded column; rows remain the initial height and scrolling stays inside. */
  fill?: boolean;
  /**
   * Shows the text in the kit's monospace font — source text such as Markdown
   * or code, where columns and spacing matter. Defaults to `false`.
   */
  monospace?: boolean;
};

const props = withDefaults(
  defineProps<
    TextareaProps & /* @vue-ignore */ Omit<TextareaHTMLAttributes, 'value' | 'spellcheck'>
  >(),
  { spellcheck: false, resizable: true }
);

const [valueModel, valueModelModifiers] = defineModel<string, 'lazy'>({ required: true });

// The attributes and listeners a caller sets belong to the `<textarea>` alone
// (`...$attrs` below). Left to fall through they would also reach the wrapper,
// which hands them to the textarea a second time: every `@keydown` ran twice.
defineOptions({ inheritAttrs: false });

const textareaRef = useTemplateRef<HTMLTextAreaElement>('textarea-element');

// The `<textarea>` itself, for what only the element can tell: the caret and
// the selection (`selectionStart`, `setSelectionRange`) and focus. `null`
// while it is not mounted. Mirrors NxTextInput's `inputElement`.
defineExpose({ inputElement: computed(() => textareaRef.value ?? null) });

// The wrapper gets the field's props, but not `spellcheck` and `resizable`:
// they are the textarea's own (an attribute and a class); handed on they would
// land on the native field a second time or as a stray attribute.
const wrapperProps = computed(
  (): Omit<typeof props, 'spellcheck' | 'resizable' | 'fill' | 'monospace'> => {
    const rest = { ...props };
    Reflect.deleteProperty(rest, 'spellcheck');
    Reflect.deleteProperty(rest, 'resizable');
    Reflect.deleteProperty(rest, 'fill');
    Reflect.deleteProperty(rest, 'monospace');
    return rest;
  }
);
</script>

<template>
  <NxInputWrapper
    v-bind="wrapperProps"
    :wrapper-style="[
      props.wrapperStyle,
      props.fill
        ? { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }
        : undefined,
    ]"
    multiline
  >
    <template #default="{ inputAttrs, textHidden }">
      <textarea
        v-if="valueModelModifiers.lazy"
        v-bind="{
          ...inputAttrs,
          ...$attrs,
          style: {
            ...inputAttrs.style,
            ...($attrs.style as {}),
            ...(props.fill ? { flex: '1 1 auto', minHeight: '0' } : {}),
          },
        }"
        ref="textarea-element"
        v-model.lazy="valueModel"
        :class="[
          `height-${props.height ?? 's'}`,
          {
            'text-hidden': textHidden,
            'not-resizable': !props.resizable || props.fill,
            monospace: props.monospace,
          },
        ]"
        :autocomplete="'off'"
        :spellcheck="props.spellcheck"
      />
      <textarea
        v-else
        v-bind="{
          ...inputAttrs,
          ...$attrs,
          style: {
            ...inputAttrs.style,
            ...($attrs.style as {}),
            ...(props.fill ? { flex: '1 1 auto', minHeight: '0' } : {}),
          },
        }"
        ref="textarea-element"
        v-model="valueModel"
        :class="[
          `height-${props.height ?? 's'}`,
          {
            'text-hidden': textHidden,
            'not-resizable': !props.resizable || props.fill,
            monospace: props.monospace,
          },
        ]"
        :autocomplete="'off'"
        :spellcheck="props.spellcheck"
      />
    </template>
  </NxInputWrapper>
</template>

<style scoped src="./style.css" />
<style scoped>
.text-hidden {
  color: transparent;
  user-select: none;
  pointer-events: none;
}
textarea {
  scrollbar-width: thin;
  scrollbar-color: var(--nxt-border-strong) transparent;
}
textarea.not-resizable {
  resize: none;
}
textarea.monospace {
  font-family: var(--nxt-font-mono);
}
</style>
